import {
  getTerraGraphCategoryColors,
  getTerraGraphSeriesColor,
  token,
} from '@terra/ui-styled-system';
import {
  isVegaLiteJsonObject,
  type VegaLiteJsonObject,
} from './vega-lite.types';

const VEGA_DEFAULT_SERIES_COLORS = new Set(['steelblue', '#4c78a8', '#4C78A8']);

export function getTerraVegaCategoryColors(): string[] {
  return getTerraGraphCategoryColors();
}

const KNOWN_VALUE_COLOR_RESOLVERS: Record<string, () => string> = {
  HOT: () => token('colors.bg.graphs.critical'),
  COLD: () => token('colors.bg.graphs.sky'),
  EXPLOITED: () => token('colors.bg.graphs.critical'),
  PASSED: () => token('colors.bg.graphs.low'),
  DUPLICATE: () => token('colors.bg.graphs.gray'),
  BLOCKED: () => token('colors.bg.graphs.medium'),
  CRITICAL: () => token('colors.bg.graphs.critical'),
  HIGH: () => token('colors.bg.graphs.high'),
  MEDIUM: () => token('colors.bg.graphs.medium'),
  LOW: () => token('colors.bg.graphs.low'),
  INFO: () => token('colors.bg.graphs.sky'),
  RUNNING: () => token('colors.bg.graphs.sky'),
  COMPLETED: () => token('colors.bg.graphs.low'),
  FAILED: () => token('colors.bg.graphs.critical'),
  PAUSED: () => token('colors.bg.graphs.medium'),
  ABORTED: () => token('colors.bg.graphs.gray'),
};

const COLOR_FIELD_HINTS = new Set([
  'status',
  'label',
  'signalstatus',
  'signal_status',
  'severity',
  'state',
  'category',
]);

export type VegaLiteThemeConfig = VegaLiteJsonObject;

export function getTerraVegaLiteThemeConfig(): VegaLiteThemeConfig {
  const categoryColors = getTerraVegaCategoryColors();
  const seriesColor = getTerraGraphSeriesColor();
  const axisLabelColor = token('colors.fg.neutral.muted');
  const axisTitleColor = token('colors.fg.neutral.subtle');
  const gridColor = token('colors.border.neutral.subtle');
  const legendLabelColor = token('colors.fg.neutral.default');
  const titleColor = token('colors.fg.default');
  const subtitleColor = token('colors.fg.muted');

  return {
    background: null,
    font: 'Inter, system-ui, sans-serif',
    axis: {
      labelColor: axisLabelColor,
      titleColor: axisTitleColor,
      gridColor,
      domainColor: gridColor,
      tickColor: gridColor,
      labelFontSize: 12,
      titleFontSize: 12,
    },
    axisX: {
      labelOverlap: true,
    },
    legend: {
      labelColor: legendLabelColor,
      titleColor: legendLabelColor,
      labelFontSize: 12,
      titleFontSize: 12,
    },
    title: {
      color: titleColor,
      subtitleColor,
      fontSize: 14,
      subtitleFontSize: 12,
      anchor: 'start',
    },
    header: {
      titleColor,
      subtitleColor,
    },
    view: {
      stroke: null,
      fill: null,
    },
    range: {
      category: categoryColors,
      ordinal: categoryColors,
    },
    mark: {
      tooltip: true,
      color: seriesColor,
    },
    bar: {
      color: seriesColor,
      cornerRadiusEnd: 4,
      strokeWidth: 0,
    },
    line: {
      color: seriesColor,
    },
    area: {
      color: seriesColor,
    },
    point: {
      color: seriesColor,
    },
    arc: {
      color: seriesColor,
    },
  };
}

function normalizeFieldName(field: string): string {
  return field.toLowerCase().replace(/[_\s-]/g, '');
}

function isVegaDefaultSeriesColor(value: unknown): boolean {
  return typeof value === 'string' && VEGA_DEFAULT_SERIES_COLORS.has(value);
}

function resolveKnownValueColor(value: string): string | undefined {
  const normalized = value.trim();

  if (normalized.length === 0) {
    return undefined;
  }

  const resolver =
    KNOWN_VALUE_COLOR_RESOLVERS[normalized] ??
    KNOWN_VALUE_COLOR_RESOLVERS[normalized.toUpperCase()];

  return resolver?.();
}

function extractInlineDataValues(
  spec: VegaLiteJsonObject,
): VegaLiteJsonObject[] {
  if (!isVegaLiteJsonObject(spec.data)) {
    return [];
  }

  const values = spec.data.values;

  return Array.isArray(values) ? values.filter(isVegaLiteJsonObject) : [];
}

function getUniqueFieldValues({
  rows,
  field,
}: {
  rows: VegaLiteJsonObject[];
  field: string;
}): string[] {
  const seen = new Set<string>();
  const ordered: string[] = [];

  for (const row of rows) {
    const value = row[field];

    if (value === undefined || value === null) {
      continue;
    }

    const key = String(value);

    if (seen.has(key)) {
      continue;
    }

    seen.add(key);
    ordered.push(key);
  }

  return ordered;
}

function hasExplicitColorRange(colorEncoding: VegaLiteJsonObject): boolean {
  if (!isVegaLiteJsonObject(colorEncoding.scale)) {
    return false;
  }

  const range = colorEncoding.scale.range;

  return Array.isArray(range) && range.length > 0;
}

function buildDomainColorScale({
  field,
  values,
}: {
  field: string;
  values: string[];
}): { domain: string[]; range: string[] } | null {
  if (values.length === 0) {
    return null;
  }

  const normalizedField = normalizeFieldName(field);
  const shouldMapKnownValues =
    COLOR_FIELD_HINTS.has(normalizedField) ||
    values.some((value) => resolveKnownValueColor(value) !== undefined);

  if (!shouldMapKnownValues) {
    return null;
  }

  const categoryColors = getTerraVegaCategoryColors();
  const domain: string[] = [];
  const range: string[] = [];

  values.forEach((value, index) => {
    domain.push(value);
    range.push(
      resolveKnownValueColor(value) ??
        categoryColors[index % categoryColors.length] ??
        token('colors.terra.9'),
    );
  });

  return { domain, range };
}

function applyColorScaleToEncoding({
  encoding,
  rows,
}: {
  encoding: VegaLiteJsonObject;
  rows: VegaLiteJsonObject[];
}): VegaLiteJsonObject {
  if (!isVegaLiteJsonObject(encoding.color)) {
    return encoding;
  }

  const color = encoding.color;

  if (isVegaDefaultSeriesColor(color.value)) {
    return {
      ...encoding,
      color: {
        ...color,
        value: getTerraGraphSeriesColor(),
      },
    };
  }

  if (color.value !== undefined) {
    return encoding;
  }

  if (hasExplicitColorRange(color)) {
    return encoding;
  }

  const field = typeof color.field === 'string' ? color.field : undefined;

  if (!field) {
    return encoding;
  }

  const values = getUniqueFieldValues({ rows, field });
  const mappedScale = buildDomainColorScale({ field, values });

  if (!mappedScale) {
    return encoding;
  }

  const existingScale = isVegaLiteJsonObject(color.scale) ? color.scale : {};

  return {
    ...encoding,
    color: {
      ...color,
      scale: {
        ...existingScale,
        domain: mappedScale.domain,
        range: mappedScale.range,
      },
    },
  };
}

function applyTerraColorScalesToSpec(
  spec: VegaLiteJsonObject,
  inheritedRows: VegaLiteJsonObject[] = [],
): VegaLiteJsonObject {
  const localRows = extractInlineDataValues(spec);
  const rows = localRows.length > 0 ? localRows : inheritedRows;
  const nextSpec: VegaLiteJsonObject = { ...spec };

  if (isVegaLiteJsonObject(nextSpec.encoding)) {
    nextSpec.encoding = applyColorScaleToEncoding({
      encoding: nextSpec.encoding,
      rows,
    });
  }

  if (Array.isArray(nextSpec.layer)) {
    nextSpec.layer = nextSpec.layer.map((layer) =>
      isVegaLiteJsonObject(layer)
        ? applyTerraColorScalesToSpec(layer, rows)
        : layer,
    );
  }

  if (Array.isArray(nextSpec.hconcat)) {
    nextSpec.hconcat = nextSpec.hconcat.map((child) =>
      isVegaLiteJsonObject(child)
        ? applyTerraColorScalesToSpec(child, rows)
        : child,
    );
  }

  if (Array.isArray(nextSpec.vconcat)) {
    nextSpec.vconcat = nextSpec.vconcat.map((child) =>
      isVegaLiteJsonObject(child)
        ? applyTerraColorScalesToSpec(child, rows)
        : child,
    );
  }

  if (Array.isArray(nextSpec.concat)) {
    nextSpec.concat = nextSpec.concat.map((child) =>
      isVegaLiteJsonObject(child)
        ? applyTerraColorScalesToSpec(child, rows)
        : child,
    );
  }

  const nestedSpec = nextSpec.spec;

  if (isVegaLiteJsonObject(nestedSpec)) {
    nextSpec.spec = applyTerraColorScalesToSpec(nestedSpec, rows);
  }

  return nextSpec;
}

function applyTerraDefaultMarkColor(
  spec: VegaLiteJsonObject,
): VegaLiteJsonObject {
  const seriesColor = getTerraGraphSeriesColor();
  const nextSpec: VegaLiteJsonObject = { ...spec };
  const mark = nextSpec.mark;

  if (isVegaLiteJsonObject(mark) && isVegaDefaultSeriesColor(mark.color)) {
    nextSpec.mark = {
      ...mark,
      color: seriesColor,
    };
  }

  if (Array.isArray(nextSpec.layer)) {
    nextSpec.layer = nextSpec.layer.map((layer) =>
      isVegaLiteJsonObject(layer) ? applyTerraDefaultMarkColor(layer) : layer,
    );
  }

  if (Array.isArray(nextSpec.hconcat)) {
    nextSpec.hconcat = nextSpec.hconcat.map((child) =>
      isVegaLiteJsonObject(child) ? applyTerraDefaultMarkColor(child) : child,
    );
  }

  if (Array.isArray(nextSpec.vconcat)) {
    nextSpec.vconcat = nextSpec.vconcat.map((child) =>
      isVegaLiteJsonObject(child) ? applyTerraDefaultMarkColor(child) : child,
    );
  }

  if (Array.isArray(nextSpec.concat)) {
    nextSpec.concat = nextSpec.concat.map((child) =>
      isVegaLiteJsonObject(child) ? applyTerraDefaultMarkColor(child) : child,
    );
  }

  const nestedSpec = nextSpec.spec;

  if (isVegaLiteJsonObject(nestedSpec)) {
    nextSpec.spec = applyTerraDefaultMarkColor(nestedSpec);
  }

  return nextSpec;
}

function stripVegaDefaultConfigColors(
  config: VegaLiteJsonObject,
): VegaLiteJsonObject {
  const markTypes = ['mark', 'bar', 'line', 'area', 'point', 'arc'] as const;

  return markTypes.reduce((nextConfig, markType) => {
    const markConfig = nextConfig[markType];

    if (
      !isVegaLiteJsonObject(markConfig) ||
      !isVegaDefaultSeriesColor(markConfig.color)
    ) {
      return nextConfig;
    }

    const { color: _vegaDefault, ...markConfigWithoutColor } = markConfig;

    return {
      ...nextConfig,
      [markType]: markConfigWithoutColor,
    };
  }, config);
}

export function applyTerraVegaLiteTheme(
  spec: VegaLiteJsonObject,
): VegaLiteJsonObject {
  const terraTheme = getTerraVegaLiteThemeConfig();
  const withColorScales = applyTerraColorScalesToSpec(spec);
  const withDefaultMarks = applyTerraDefaultMarkColor(withColorScales);
  const existingConfig = isVegaLiteJsonObject(withDefaultMarks.config)
    ? stripVegaDefaultConfigColors(withDefaultMarks.config)
    : {};

  return {
    ...withDefaultMarks,
    config: deepMergeRecords(terraTheme, existingConfig),
  };
}

function deepMergeRecords(
  base: VegaLiteJsonObject,
  override: VegaLiteJsonObject,
): VegaLiteJsonObject {
  const merged: VegaLiteJsonObject = { ...base };

  for (const [key, overrideValue] of Object.entries(override)) {
    const baseValue = merged[key];

    if (
      isVegaLiteJsonObject(baseValue) &&
      isVegaLiteJsonObject(overrideValue)
    ) {
      merged[key] = deepMergeRecords(baseValue, overrideValue);
      continue;
    }

    merged[key] = overrideValue;
  }

  return merged;
}
